import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject, catchError, filter, finalize, switchMap, take, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { AuthStateService } from '../states/auth.state';
import { Router } from '@angular/router';
import { AGE_VERIFICATION_REQUIRED } from '../services/age-verification.service';
import { ADMIN_PREVIEW_HEADER, AdminPreviewService } from '../services/admin-preview.service';

let isRefreshing = false;
const refreshTokenSubject = new BehaviorSubject<string | null>(null);

const isAuthEndpoint = (url: string): boolean => {
  return url.includes('/auth/login')
    || url.includes('/auth/refresh')
    || url.includes('/auth/google')
    || url.includes('/auth/signup')
    || url.includes('/auth/quick-login');
};

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const platformId = inject(PLATFORM_ID);
  const isBrowser = isPlatformBrowser(platformId);
  const http = inject(HttpClient);
  const authState = inject(AuthStateService);
  const router = inject(Router);

  let token: string | null = null;
  let refreshToken: string | null = null;

  if (isBrowser) {
    try {
      const auth = localStorage.getItem('auth');
      const parsed = auth ? JSON.parse(auth) : null;
      token = parsed?.token ?? null;
      refreshToken = parsed?.refreshToken ?? null;
    } catch {
      token = null;
      refreshToken = null;
    }
  }

  // Admin panel preview: unlock removed/draft content for the one item
  // this tab was opened for (header is only added to that item's GET).
  const previewToken = isBrowser ? inject(AdminPreviewService).tokenForRequest(req.method, req.url) : null;
  if (previewToken) {
    req = req.clone({ setHeaders: { [ADMIN_PREVIEW_HEADER]: previewToken } });
  }

  const authReq = token
    ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
    : req;

  return next(authReq).pipe(
    catchError((err: HttpErrorResponse) => {
      // API says this account hasn't passed VerifyMy yet → go verify.
      if (isBrowser && err.status === 403 && err.error?.code === AGE_VERIFICATION_REQUIRED) {
        authState.updateUser({ ageVerified: false, ageVerificationRequired: true });
        if (!router.url.startsWith('/age-verification')) {
          try { sessionStorage.setItem('ageVerificationReturnUrl', router.url); } catch { /* ignore */ }
          router.navigate(['/age-verification']);
        }
        return throwError(() => err);
      }

      if (!isBrowser || err.status !== 401 || isAuthEndpoint(req.url)) {
        return throwError(() => err);
      }

      if (!refreshToken) {
        // Logged in on paper but nothing to renew with — session is over.
        authState.endSession('expired');
        return throwError(() => err);
      }

      if (!isRefreshing) {
        isRefreshing = true;
        refreshTokenSubject.next(null);

        const clientId = authState.getClientId();

        return http.post<any>(`${environment.apiUrl}/auth/refresh`, {
          refreshToken,
          clientId: clientId ?? undefined,
        }).pipe(
          switchMap((res) => {
            const newAccessToken = res?.data?.accessToken;
            if (!newAccessToken) {
              authState.endSession('expired');
              return throwError(() => err);
            }

            const stored = localStorage.getItem('auth');
            const current = stored ? JSON.parse(stored) : {};

            authState.setAuth({
              ...current,
              token: newAccessToken,
            });

            refreshTokenSubject.next(newAccessToken);

            return next(req.clone({
              setHeaders: { Authorization: `Bearer ${newAccessToken}` }
            }));
          }),
          catchError((refreshErr: HttpErrorResponse) => {
            // The server refused to renew: the session was revoked (password
            // changed/reset, signed out on another device), expired, or the
            // account was disabled. Send the user to login instead of leaving
            // them on a page whose every request now fails.
            // Network errors / 5xx are not a verdict on the session — keep it.
            if (refreshErr.status >= 400 && refreshErr.status < 500) {
              authState.endSession(
                refreshErr.status === 403 ? 'disabled' : 'expired',
                refreshErr.status === 403 ? refreshErr.error?.message : undefined,
              );
            }
            return throwError(() => refreshErr);
          }),
          finalize(() => {
            isRefreshing = false;
          })
        );
      }

      return refreshTokenSubject.pipe(
        filter((t): t is string => !!t),
        take(1),
        switchMap((newAccessToken) => next(req.clone({
          setHeaders: { Authorization: `Bearer ${newAccessToken}` }
        })))
      );
    })
  );
};
